import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink, Store, MapPin } from 'lucide-react';
import { SuggestDealerDialog } from './SuggestDealerDialog';

interface DealerLinksSectionProps {
  modelId: string;
  modelName: string;
  brandName: string;
}

interface DealerLink {
  id: string;
  dealer_name: string;
  website_url: string;
  location: string | null;
}

export function DealerLinksSection({ modelId, modelName, brandName }: DealerLinksSectionProps) {
  const { data: dealerLinks = [] } = useQuery({
    queryKey: ['dealer-links', modelId],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_public_dealer_links', { _model_id: modelId });
      if (error) throw error;
      return ((data || []) as any[]).map((d) => ({
        id: d.id,
        dealer_name: d.dealer_name,
        website_url: d.website_url,
        location: d.location,
      })) as DealerLink[];
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 p-4 pb-3 sm:p-5 sm:pb-4">
        <CardTitle className="flex min-w-0 items-center gap-2 font-display text-[19px] sm:text-xl">
          <Store className="w-[18px] h-[18px] shrink-0 text-primary" />
          Forhandlere
        </CardTitle>
        <SuggestDealerDialog modelId={modelId} modelName={modelName} brandName={brandName} />
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">

        {dealerLinks.length > 0 ? (
          <div className="space-y-3">
            {dealerLinks.map((link) => (
              <a
                key={link.id}
                href={link.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/5 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Store className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold group-hover:text-primary transition-colors">
                    {link.dealer_name}
                  </h4>
                  {link.location && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {link.location}
                    </p>
                  )}
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0" />
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <p className="mb-2">Ingen forhandlere tilføjet endnu</p>
            <p className="text-sm">Kender du en forhandler af denne elbil?</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
